import { useEffect, useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import { BluebirdContactForm } from "@/components/bluebird/ContactForm";
import { color, font, radius, shadow, type } from "@/lib/design-tokens";
import { trackEvent } from "@/lib/analytics";

function topicForPath(pathname: string) {
  if (pathname === "/book") return "test-ride";
  if (pathname === "/services") return "service";
  if (pathname.startsWith("/bikes")) return "sales";
  return undefined;
}

export function ContactWidget({
  aboveMobileBar = false,
  inline = false,
}: {
  aboveMobileBar?: boolean;
  inline?: boolean;
}) {
  const { url } = usePage();
  const pathname = url.split(/[?#]/, 1)[0];
  const [open, setOpen] = useState(false);
  const launcher = useRef<HTMLButtonElement | null>(null);
  const dialog = useRef<HTMLDivElement | null>(null);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = Array.from(
        dialog.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]'
        )
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      launcher.current?.focus();
    };
  }, [open]);

  // The dedicated Contact page already puts the form in the main reading flow.
  if (pathname === "/contact") return null;

  const openModal = () => {
    setOpen(true);
    trackEvent("contact_widget_open", { page_path: pathname });
  };

  return (
    <div
      data-contact-widget=""
      className={`bluebird contact-widget${aboveMobileBar ? " contact-widget--above-mobile-bar" : ""}${inline ? " contact-widget--inline" : ""}`}
    >
      <button
        ref={launcher}
        type="button"
        data-testid="contact-widget-launcher"
        className="contact-widget-launcher"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="contact-widget-dialog"
        onClick={openModal}
      >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        </svg>
        Contact us
      </button>

      {open && (
        <div
          className="contact-widget-overlay"
          data-testid="contact-widget-modal"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div
            ref={dialog}
            id="contact-widget-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-widget-title"
            className="contact-widget-modal"
            tabIndex={-1}
            style={{ background: color.white, borderRadius: radius.panel, boxShadow: shadow.visitCard }}
          >
            <div className="contact-widget-heading">
              <div>
                <div
                  style={{
                    fontFamily: font.mono,
                    fontSize: "11px",
                    letterSpacing: ".13em",
                    textTransform: "uppercase",
                    color: color.blue,
                  }}
                >
                  Front Range Cargo Bikes
                </div>
                <h2 id="contact-widget-title" style={{ margin: "8px 0 0", fontSize: type.h3, color: color.ink }}>
                  Send us a message
                </h2>
                <p style={{ margin: "8px 0 0", fontSize: "15px", lineHeight: 1.5, color: color.inkMuted }}>
                  Ask about bikes, test rides, service, or Colorado savings.
                </p>
              </div>
              <button type="button" className="contact-widget-close" aria-label="Close contact form" onClick={() => setOpen(false)}>
                <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <BluebirdContactForm presetTopic={topicForPath(pathname)} returnTo={pathname} />
          </div>
        </div>
      )}
    </div>
  );
}
